import React from 'react';
import { ShieldCheck } from 'lucide-react';

export const PrivacyPage: React.FC = () => {
  return (
    <div className="canvas-dark" style={{ minHeight: '100vh', paddingTop: '130px', paddingBottom: '80px', backgroundColor: '#121212' }}>
      
      {/* Hero */}
      <header className="container" style={{ padding: '0 20px 48px', maxWidth: '880px' }}>
        <div className="eyebrow" style={{ marginBottom: '12px' }}>Privacy &amp; Data Protection</div>
        <h1 
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: 'clamp(32px, 5vw, 48px)',
            fontWeight: 400,
            lineHeight: 1.2,
            letterSpacing: '-1px',
            color: '#FFFFFF',
            margin: '0 0 16px'
          }}
        >
          Privacy Policy
        </h1>
        <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: '15px', color: '#D7D7D7', lineHeight: 1.6, margin: 0 }}>
          Effective Date: January 1, 2026 &bull; Last Updated: September 2026. <br />
          Draway Auth is built under a zero-trust, zero-knowledge security model. We treat data privacy as a core engineering invariant, not a legal compliance afterthought.
        </p>
      </header>

      {/* Content Layout */}
      <div className="container" style={{ maxWidth: '880px', padding: '0 20px' }}>
        <div 
          style={{ 
            backgroundColor: '#1A1A1A', 
            border: '1px solid rgba(255, 255, 255, 0.08)', 
            borderRadius: '8px', 
            padding: '40px 36px', 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '36px' 
          }}
        >
          {/* Privacy Architecture Highlight */}
          <div 
            style={{ 
              backgroundColor: '#121212', 
              border: '1px solid #374151', 
              borderRadius: '8px', 
              padding: '22px 24px' 
            }}
          >
            <h4 style={{ color: '#FFFFFF', fontSize: '15px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
              <ShieldCheck size={18} color="#6D7CFF" /> Zero-Knowledge Identity Commitments
            </h4>
            <ul style={{ color: '#D7D7D7', fontSize: '14px', marginTop: '12px', paddingLeft: '20px', lineHeight: 1.7 }}>
              <li><strong style={{ color: '#FFFFFF' }}>Zero Plaintext Passwords:</strong> We never receive, process, or store plaintext passwords. Passwords are hashed using memory-hard Argon2id (64MB per hash).</li>
              <li><strong style={{ color: '#FFFFFF' }}>Encrypted MFA Seeds:</strong> Multi-factor seeds and emergency recovery keys are vaulted using AES-256-GCM authenticated ciphertexts (<code>iv:authTag:content</code>).</li>
              <li><strong style={{ color: '#FFFFFF' }}>Biometric Privacy:</strong> WebAuthn Passkeys never transmit biometric data (FaceID/TouchID). Only cryptographic public keys are verified on our servers.</li>
              <li><strong style={{ color: '#FFFFFF' }}>No Data Monetization:</strong> We do not sell, rent, or monetize developer or end-user identity telemetry to data brokers or advertising networks.</li>
            </ul>
          </div>

          {/* Section 1 */}
          <section>
            <h3 style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: 500, marginBottom: '12px' }}>1. Information We Collect &amp; Process</h3>
            <p style={{ color: '#D7D7D7', fontSize: '14px', lineHeight: 1.7 }}>
              When providing authentication infrastructure, Draway Auth processes data on behalf of our developer customers (as a Data Processor under GDPR Article 28):
            </p>
            <ul style={{ color: '#D7D7D7', fontSize: '14px', marginTop: '10px', paddingLeft: '20px', lineHeight: 1.7 }}>
              <li><strong style={{ color: '#FFFFFF' }}>Account Identifiers:</strong> User email addresses, phone numbers (for SMS/WhatsApp OTP delivery), and organization tenant IDs.</li>
              <li><strong style={{ color: '#FFFFFF' }}>Cryptographic Credentials:</strong> Argon2id password hashes, salts, WebAuthn FIDO2 public keys, and AES-256-GCM encrypted TOTP seeds.</li>
              <li><strong style={{ color: '#FFFFFF' }}>Session Telemetry:</strong> IP addresses, user-agent signatures, device canvas hashes (for toll-fraud bot prevention), and authentication timestamps.</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section>
            <h3 style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: 500, marginBottom: '12px' }}>2. How We Use Authenticated Data</h3>
            <p style={{ color: '#D7D7D7', fontSize: '14px', lineHeight: 1.7 }}>
              Data processed through Draway Auth is utilized exclusively for:
            </p>
            <ul style={{ color: '#D7D7D7', fontSize: '14px', marginTop: '10px', paddingLeft: '20px', lineHeight: 1.7 }}>
              <li>Validating identity credentials and issuing cryptographically signed JWT tokens.</li>
              <li>Executing multi-factor challenges (WhatsApp OTP, SMS, email verification, passkey assertion).</li>
              <li>Defending against brute-force attacks, credential stuffing, and international telephony pumping (toll fraud).</li>
              <li>Generating immutable audit logs for customer compliance requirements (SOC2 Type II, HIPAA).</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h3 style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: 500, marginBottom: '12px' }}>3. Sub-Processors &amp; Infrastructure Partners</h3>
            <p style={{ color: '#D7D7D7', fontSize: '14px', lineHeight: 1.7 }}>
              Draway Auth partners with enterprise-grade cloud providers subject to strict Data Processing Agreements:
            </p>
            <div style={{ marginTop: '16px', overflowX: 'auto', border: '1px solid #374151', borderRadius: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', color: '#D7D7D7' }}>
                <thead>
                  <tr style={{ background: '#151515', borderBottom: '1px solid #374151', textAlign: 'left' }}>
                    <th style={{ padding: '12px 16px', color: '#FFFFFF', fontWeight: 500 }}>Sub-Processor</th>
                    <th style={{ padding: '12px 16px', color: '#FFFFFF', fontWeight: 500 }}>Purpose</th>
                    <th style={{ padding: '12px 16px', color: '#FFFFFF', fontWeight: 500 }}>Data Region</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #374151' }}>
                    <td style={{ padding: '12px 16px', color: '#6D7CFF' }}>Cloudflare, Inc.</td>
                    <td style={{ padding: '12px 16px' }}>Edge CDN, DDoS Defense &amp; Turnstile</td>
                    <td style={{ padding: '12px 16px' }}>Global Edge</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #374151' }}>
                    <td style={{ padding: '12px 16px', color: '#6D7CFF' }}>Amazon Web Services (AWS)</td>
                    <td style={{ padding: '12px 16px' }}>Cloud Compute &amp; Isolated VPC Hosting</td>
                    <td style={{ padding: '12px 16px' }}>US / EU / APAC Pinned</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #374151' }}>
                    <td style={{ padding: '12px 16px', color: '#6D7CFF' }}>Meta Platforms (Cloud API)</td>
                    <td style={{ padding: '12px 16px' }}>WhatsApp Business OTP Delivery</td>
                    <td style={{ padding: '12px 16px' }}>Global Telecom</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px 16px', color: '#6D7CFF' }}>MongoDB Atlas</td>
                    <td style={{ padding: '12px 16px' }}>Encrypted Customer Database Tier</td>
                    <td style={{ padding: '12px 16px' }}>Dedicated Tenant Clusters</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 4 */}
          <section>
            <h3 style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: 500, marginBottom: '12px' }}>4. Cookie Policy &amp; Storage</h3>
            <p style={{ color: '#D7D7D7', fontSize: '14px', lineHeight: 1.7 }}>
              Draway Auth uses cookies solely for core authentication session state. We do not use third-party tracking or advertising cookies.
              <br /><br />
              <strong style={{ color: '#FFFFFF' }}>Session Cookie:</strong> <code>draway_at</code> is set with <code>HttpOnly</code>, <code>Secure</code>, and <code>SameSite: Lax</code> attributes, preventing cross-site scripting (XSS) extraction and cross-site request forgery (CSRF).
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h3 style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: 500, marginBottom: '12px' }}>5. GDPR &amp; CCPA User Rights</h3>
            <p style={{ color: '#D7D7D7', fontSize: '14px', lineHeight: 1.7 }}>
              In compliance with European (GDPR) and California (CCPA/CPRA) privacy laws, end-users have the right to:
            </p>
            <ul style={{ color: '#D7D7D7', fontSize: '14px', marginTop: '10px', paddingLeft: '20px', lineHeight: 1.7 }}>
              <li><strong style={{ color: '#FFFFFF' }}>Right to be Forgotten:</strong> Permanently delete all associated authentication profiles and encrypted keys via our automated Erasure API.</li>
              <li><strong style={{ color: '#FFFFFF' }}>Data Portability:</strong> Export all identity metadata in standardized machine-readable JSON format.</li>
              <li><strong style={{ color: '#FFFFFF' }}>Rectification:</strong> Update or replace credentials, passkeys, and verified phone numbers instantly.</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section>
            <h3 style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: 500, marginBottom: '12px' }}>6. Data Protection Officer (DPO) Contact</h3>
            <p style={{ color: '#D7D7D7', fontSize: '14px', lineHeight: 1.7 }}>
              For data access requests, GDPR compliance inquiries, or Data Processing Addendums (DPAs), contact our Data Protection Officer at <a href="mailto:privacy@draway.in" style={{ color: '#6D7CFF', textDecoration: 'underline' }}>privacy@draway.in</a>.
            </p>
          </section>

        </div>
      </div>

    </div>
  );
};

export default PrivacyPage;
